/**
 * DailyStreakManager - Retention Gamification (localStorage-based)
 *
 * Tracks consecutive daily visits to drive recurring user engagement.
 * Awards streak milestones (3-day, 7-day, 30-day) for unlocking rewards.
 *
 * Storage:
 * - dinoStreakCount: number of consecutive days
 * - dinoStreakLastVisit: ISO date string (YYYY-MM-DD) of last visit
 * - dinoStreakLongest: highest streak ever reached
 * - dinoStreakBadges: JSON array of unlocked badge IDs
 */

const STORAGE_KEY_COUNT = 'dinoStreakCount';
const STORAGE_KEY_LAST = 'dinoStreakLastVisit';
const STORAGE_KEY_LONGEST = 'dinoStreakLongest';
const STORAGE_KEY_BADGES = 'dinoStreakBadges';

export interface StreakState {
  count: number;
  lastVisit: string | null;
  longest: number;
  badges: string[];
  /** Whether today's visit was just registered (true on first call per day) */
  newVisitToday: boolean;
  /** Whether a new badge was just unlocked on this visit */
  newBadgeUnlocked: string | null;
}

export interface StreakBadge {
  id: string;
  name: string;
  description: string;
  threshold: number;
  unlocksCosmetic?: string;
}

export const STREAK_BADGES: StreakBadge[] = [
  {
    id: 'streak_3',
    name: 'Veteran',
    description: '3-day streak — Welcome back',
    threshold: 3,
    unlocksCosmetic: 'gold',
  },
  {
    id: 'streak_7',
    name: 'Weekly Runner',
    description: '7-day streak — Keep it going',
    threshold: 7,
  },
  {
    id: 'streak_30',
    name: 'Dino Devotee',
    description: '30-day streak — True legend',
    threshold: 30,
  },
];

function getTodayISO(): string {
  // Use local date in YYYY-MM-DD format (matches user's timezone for streak fairness)
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function daysBetween(isoA: string, isoB: string): number {
  const a = new Date(isoA + 'T00:00:00');
  const b = new Date(isoB + 'T00:00:00');
  const diffMs = b.getTime() - a.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function readStreakBadges(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_BADGES);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === 'string');
    return [];
  } catch {
    return [];
  }
}

function writeStreakBadges(badges: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY_BADGES, JSON.stringify(badges));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Read the current streak state (read-only, does not register a visit).
 */
export function getStreakState(): StreakState {
  if (typeof window === 'undefined') {
    return { count: 0, lastVisit: null, longest: 0, badges: [], newVisitToday: false, newBadgeUnlocked: null };
  }

  try {
    const count = parseInt(localStorage.getItem(STORAGE_KEY_COUNT) || '0', 10) || 0;
    const lastVisit = localStorage.getItem(STORAGE_KEY_LAST);
    const longest = parseInt(localStorage.getItem(STORAGE_KEY_LONGEST) || '0', 10) || 0;
    const badges = readStreakBadges();
    return { count, lastVisit, longest, badges, newVisitToday: false, newBadgeUnlocked: null };
  } catch {
    return { count: 0, lastVisit: null, longest: 0, badges: [], newVisitToday: false, newBadgeUnlocked: null };
  }
}

/**
 * Register a visit for today and update streak counter.
 * - Same day visit: no change
 * - Consecutive day: count++
 * - Gap day: streak resets to 1
 *
 * Returns updated state with newVisitToday flag and any newly unlocked badge.
 */
export function registerVisit(): StreakState {
  if (typeof window === 'undefined') {
    return { count: 0, lastVisit: null, longest: 0, badges: [], newVisitToday: false, newBadgeUnlocked: null };
  }

  const today = getTodayISO();
  const current = getStreakState();

  // Already visited today - no change
  if (current.lastVisit === today) {
    return { ...current, newVisitToday: false };
  }

  let newCount: number;
  if (current.lastVisit === null) {
    newCount = 1; // First-ever visit
  } else {
    const gap = daysBetween(current.lastVisit, today);
    if (gap === 1) {
      newCount = current.count + 1; // Consecutive day
    } else if (gap === 0) {
      newCount = current.count; // Same day (defensive, should be caught above)
    } else {
      newCount = 1; // Streak broken - reset
    }
  }

  const newLongest = Math.max(current.longest, newCount);

  // Check for newly unlocked badges
  let newBadgeUnlocked: string | null = null;
  const updatedBadges = [...current.badges];
  for (const badge of STREAK_BADGES) {
    if (newCount >= badge.threshold && !updatedBadges.includes(badge.id)) {
      updatedBadges.push(badge.id);
      newBadgeUnlocked = badge.id;
      break; // Only announce one badge per visit
    }
  }

  // Persist
  try {
    localStorage.setItem(STORAGE_KEY_COUNT, String(newCount));
    localStorage.setItem(STORAGE_KEY_LAST, today);
    localStorage.setItem(STORAGE_KEY_LONGEST, String(newLongest));
    if (newBadgeUnlocked) writeStreakBadges(updatedBadges);
  } catch {
    // localStorage unavailable
  }

  return {
    count: newCount,
    lastVisit: today,
    longest: newLongest,
    badges: updatedBadges,
    newVisitToday: true,
    newBadgeUnlocked,
  };
}

/**
 * Check if a specific badge has been unlocked.
 */
export function hasBadge(badgeId: string): boolean {
  return readStreakBadges().includes(badgeId);
}

/**
 * Get badge definition by ID.
 */
export function getBadgeById(badgeId: string): StreakBadge | undefined {
  return STREAK_BADGES.find((b) => b.id === badgeId);
}
